import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Award, Clock, Shield, Users, Microscope, MapPin } from 'lucide-react';
import { useContent } from '@/hooks/useContent';

gsap.registerPlugin(ScrollTrigger);

const WhyElhamyLabs = () => {
  const { t } = useContent();
  const sectionRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);

  const features = [
    {
      icon: Award,
      titleKey: 'home.why.accuracy.title',
      descriptionKey: 'home.why.accuracy.description',
    },
    {
      icon: Clock,
      titleKey: 'home.why.speed.title',
      descriptionKey: 'home.why.speed.description',
    },
    {
      icon: Shield,
      titleKey: 'home.why.certified.title',
      descriptionKey: 'home.why.certified.description',
    },
    {
      icon: Microscope,
      titleKey: 'home.why.equipment.title',
      descriptionKey: 'home.why.equipment.description',
    },
    {
      icon: Users,
      titleKey: 'home.why.team.title',
      descriptionKey: 'home.why.team.description',
    },
    {
      icon: MapPin,
      titleKey: 'home.why.branches.title',
      descriptionKey: 'home.why.branches.description',
    },
  ];

  useEffect(() => {
    const section = sectionRef.current;
    const items = itemsRef.current;

    if (!section || !items) return;

    gsap.fromTo(
      items.children,
      {
        opacity: 0,
        x: -50,
      },
      {
        opacity: 1,
        x: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 80%',
        },
      }
    );
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-gradient-to-br from-light-accent to-light-secondary dark:from-dark-secondary dark:to-dark-primary"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-light-text dark:text-dark-text">
            {t('home.why.title')}
          </h2>
          <p className="text-lg text-light-text-secondary dark:text-dark-text-secondary">
            {t('home.why.subtitle')}
          </p>
        </div>

        <div
          ref={itemsRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
            <div
              key={index}
              className="p-6 bg-light-primary dark:bg-dark-secondary rounded-xl shadow-lg"
            >
              <div className="w-16 h-16 mb-4 rounded-full bg-brand-purple-base/10 dark:bg-brand-orange-base/10 flex items-center justify-center">
                <Icon
                  size={32}
                  className="text-brand-purple-base dark:text-brand-orange-base"
                />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-light-text dark:text-dark-text">
                {t(feature.titleKey)}
              </h3>
              <p className="text-light-text-secondary dark:text-dark-text-secondary">
                {t(feature.descriptionKey)}
              </p>
            </div>
          );
        })}
        </div>
      </div>
    </section>
  );
};

export default WhyElhamyLabs;

